/**
 * Stock Overview - UI แบบ foodorder เชื่อมระบบคลัง thunderbee
 * ใช้ getDataWithComp (Tb_Set_Item, API_1101_WH_ITEM_ONHAND, Tb_Set_WH)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Input,
  Tag,
  Select,
  Row,
  Col,
  Typography,
  Table,
  Space,
  message,
} from "antd";
import { SearchOutlined, PlusOutlined, HistoryOutlined, CopyOutlined } from "@ant-design/icons";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import DataTable from "../../../components/DataTable";
import ProductModal from "../../components/Modal/ProductModal";
import { getDataWithComp, getAlert } from "../../../utils/SamuiUtils";
import { submitItems } from "../../../utils/services";
import { cacheGet, cacheSet } from "../../../utils/StockCache";

const { Title } = Typography;

const SESSION_KEY = "stock_overview_session";

const loadSession = () => {
  try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}"); } catch { return {}; }
};

const saveSession = (patch) => {
  try {
    const prev = loadSession();
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...prev, ...patch }));
  } catch {}
};

export default function StockOverviewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const _s = loadSession();
  const [data, setData] = useState(_s.data || []);
  const hasInitialData = useRef(_s.data?.length > 0);
  const [loading, setLoading] = useState(!hasInitialData.current);
  const [searchText, setSearchText] = useState(_s.searchText || "");
  const [filterGroup, setFilterGroup] = useState(_s.filterGroup ?? null);
  const [groups, setGroups] = useState([]);
  const [selectedRow, setSelectedRow] = useState(_s.selectedRow || null);
  const [onhandRows, setOnhandRows] = useState(_s.onhandRows || []);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [whDataList, setWhDataList] = useState([]);
  const [filterWH, setFilterWH] = useState(_s.filterWH ?? null);
  const [filterLowStock, setFilterLowStock] = useState(_s.filterLowStock ?? false);
  const allOnhandRef = useRef([]);

  const fetchWarehouses = async () => {
    try {
      const rows = await getDataWithComp("Tb_Set_WH", "AND WH_Status = 'Y' ORDER BY WH_Name ASC");
      setWhDataList(Array.isArray(rows) ? rows : []);
    } catch {
      setWhDataList([]);
    }
  };

  const fetchStock = async (searchVal = "", groupVal = null, silent = false) => {
    if (!silent) setLoading(true);
    try {
      let cond = "AND Item_Status = 'Y'";
      if (searchVal) {
        const escaped = searchVal.replace(/'/g, "''");
        cond += ` AND (Item_Name LIKE '%${escaped}%' OR Item_Code LIKE '%${escaped}%')`;
      }
      if (groupVal != null) {
        cond += ` AND Item_Group = ${Number(groupVal)}`;
      }
      cond += " ORDER BY Item_Name ASC";

      // โชว์ items ทันทีที่ดึงได้ ไม่รอ onhand
      const itemList = await getDataWithComp("Tb_Set_Item", cond);
      const items = Array.isArray(itemList) ? itemList : [];
      setData(items);
      if (!silent) setLoading(false);

      // ดึง onhand เงียบๆ แล้ว merge ทีหลัง
      const onhandList = await getDataWithComp("API_1101_WH_ITEM_ONHAND", "");
      const onhandAll = Array.isArray(onhandList) ? onhandList : [];
      allOnhandRef.current = onhandAll;
      const onhandMap = {};
      onhandAll.forEach((r) => {
        const id = Number(r.Item_Id);
        const qty = Number(r.Item_Onhand ?? r.Item_OnHand ?? r.Item_Qty ?? r.Onhand ?? 0);
        if (Number.isFinite(id)) {
          onhandMap[id] = (onhandMap[id] ?? 0) + (Number.isFinite(qty) ? qty : 0);
        }
      });

      const merged = items.map((r) => ({
        ...r,
        Item_Onhand: onhandMap[Number(r.Item_Id)] ?? r.Item_Onhand ?? r.Item_OnHand ?? 0,
      }));
      setData(merged);
      // cache ลง IndexedDB เงียบๆ (ไม่ต้องรอ)
      cacheSet("stock_overview_items", merged);
      cacheSet("stock_overview_onhand", onhandAll);
      return merged;
    } catch (err) {
      getAlert("FAILED", err?.message || "โหลดข้อมูลสินค้าไม่สำเร็จ");
      if (!silent) setData([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const rows = await getDataWithComp("Tb_Set_ItmGroup", "AND Group_Status = 'Y' ORDER BY Group_Name");
      setGroups(Array.isArray(rows) ? rows : []);
    } catch {
      setGroups([]);
    }
  };

  const SelectedRowData = async (record) => {
    setSelectedRow(record);
    if (!record?.Item_Id) return;
    try {
      const [onhandArr, historyArr] = await Promise.all([
        getDataWithComp(
          "API_1101_WH_ITEM_ONHAND",
          `AND Item_Id = ${record.Item_Id} ORDER BY WH_Name ASC`
        ),
        getDataWithComp(
          "WH_ITEM_STC",
          `AND Item_Id = ${record.Item_Id} ORDER BY STC_SEQ DESC`
        ),
      ]);
      const onhand = Array.isArray(onhandArr) ? onhandArr : [];
      const history = Array.isArray(historyArr) ? historyArr : [];
      const enriched = onhand.map((r) => ({
        ...r,
        Last_STC_Date:
          r.Last_STC_Date ??
          r.last_stc_date ??
          r.STC_Date ??
          r.stc_date ??
          r.Last_Date ??
          r.last_date,
      }));
      setOnhandRows(enriched);
    } catch {
      setOnhandRows([]);
    }
  };

  const groupMap = useMemo(() => {
    const map = {};
    for (const g of groups) map[g.Group_Id] = g.Group_Name;
    return map;
  }, [groups]);

  const filtered = useMemo(() => {
    let result = data || [];
    if (filterWH) {
      const whId = Number(filterWH);
      const itemIdsInWH = new Set(
        allOnhandRef.current
          .filter((r) => Number(r.WH_Id) === whId)
          .map((r) => Number(r.Item_Id))
      );
      const whOnhandMap = {};
      allOnhandRef.current
        .filter((r) => Number(r.WH_Id) === whId)
        .forEach((r) => { whOnhandMap[Number(r.Item_Id)] = Number(r.Item_Onhand ?? r.Item_OnHand ?? r.Item_Qty ?? r.Onhand ?? 0); });
      result = result
        .filter((r) => itemIdsInWH.has(Number(r.Item_Id)))
        .map((r) => ({ ...r, Item_Onhand: whOnhandMap[Number(r.Item_Id)] ?? 0 }));
    }
    if (filterLowStock) {
      result = result.filter((r) => {
        const onhand = Number(r.Item_Onhand ?? 0);
        const popoint = Number(r.Item_PoPoint ?? 0);
        return popoint > 0 && onhand < popoint;
      });
    }
    return result;
  }, [data, filterWH, filterLowStock]);

  const onhandByWarehouse = useMemo(() => {
    return (onhandRows || [])
      .filter((r) => r && r.WH_Id != null)
      .map((r) => ({
        Item_Id: r.Item_Id,
        WH_Id: r.WH_Id,
        WH_Name: r.WH_Name || "-",
        Item_Onhand: r.Item_Onhand ?? r.Item_Qty ?? r.CURRENT_QNT ?? r.Item_OnHand ?? 0,
        Last_STC_Date: r.Last_STC_Date,
      }))
      .sort((a, b) => {
        if (!a.Last_STC_Date && !b.Last_STC_Date) return 0;
        if (!a.Last_STC_Date) return 1;
        if (!b.Last_STC_Date) return -1;
        return new Date(b.Last_STC_Date) - new Date(a.Last_STC_Date);
      });
  }, [onhandRows]);

  // บันทึก session เมื่อค่าเปลี่ยน
  useEffect(() => { saveSession({ searchText }); }, [searchText]);
  useEffect(() => { saveSession({ filterGroup }); }, [filterGroup]);
  useEffect(() => { saveSession({ filterWH }); }, [filterWH]);
  useEffect(() => { saveSession({ filterLowStock }); }, [filterLowStock]);
  useEffect(() => { saveSession({ selectedRow, onhandRows }); }, [selectedRow, onhandRows]);

  // หลัง data โหลดเสร็จ ถ้ามี selectedRow จาก session ให้ sync onhand ใหม่ + scroll ไปหาแถวนั้น
  useEffect(() => {
    if (data.length > 0 && selectedRow?.Item_Id) {
      if (onhandRows.length === 0) {
        SelectedRowData(selectedRow);
      }
      setTimeout(() => {
        const el = document.querySelector(".ant-table-row-selected");
        if (el) el.scrollIntoView({ block: "center", behavior: "instant" });
      }, 300);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // กลับมาจาก stock-detail ให้พยายาม active แถวเดิมจาก state โดยตรง
  useEffect(() => {
    if (!data.length) return;
    const selectedItemId = location.state?.selectedItemId;
    if (!selectedItemId) return;
    const selectedWhId = location.state?.selectedWhId;
    const found =
      data.find((r) => String(r.Item_Id) === String(selectedItemId)) || null;
    if (!found) return;
    setSelectedRow(found);
    SelectedRowData(found);
    setTimeout(() => {
      const el = document.querySelector(".ant-table-row-selected");
      if (el) el.scrollIntoView({ block: "center", behavior: "instant" });
    }, 300);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, location.state]);

  useEffect(() => {
    const init = async () => {
      // sessionStorage มีข้อมูลอยู่แล้ว (synchronous) → fetch เงียบๆ เลย
      if (hasInitialData.current) {
        fetchStock(searchText, filterGroup, true);
        fetchGroups();
        fetchWarehouses();
        return;
      }
      // ไม่มี sessionStorage → ลองอ่าน IndexedDB ก่อน
      const [cached, cachedOnhand] = await Promise.all([
        cacheGet("stock_overview_items"),
        cacheGet("stock_overview_onhand"),
      ]);
      if (cached?.length > 0) {
        setData(cached);
        setLoading(false);
        if (cachedOnhand?.length > 0) allOnhandRef.current = cachedOnhand;
        fetchStock(searchText, filterGroup, true);
      } else {
        fetchStock(searchText, filterGroup, false);
      }
      fetchGroups();
      fetchWarehouses();
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (val) => {
    setSearchText(val);
  };

  const handleSearchCommit = async () => {
    setSelectedRow(null);
    setOnhandRows([]);
    const result = await fetchStock(searchText, filterGroup);
    if (result && result.length > 0) {
      SelectedRowData(result[0]);
    }
  };

  const handleGroup = (val) => {
    const v = val ?? null;
    setFilterGroup(v);
    setSelectedRow(null);
    setOnhandRows([]);
    fetchStock(searchText, v);
  };

  const formatTH = (v) => {
    if (!v) return "-";
    const m = String(v).match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
    if (!m) return String(v);
    const [, yr, mo, dy, hh, mm, ss] = m;
    return `${dy}/${mo}/${Number(yr) + 543} ${hh}:${mm}:${ss}`;
  };

  const columnsOnHand = [
    { title: "#", width: 50, align: "center", render: (_, __, i) => i + 1 },
    {
      title: "คลัง", dataIndex: "WH_Name", width: 100, align: "center",
      render: (v, record) => (
        <a
          onClick={(e) => {
            e.stopPropagation();
            setFilterWH(record.WH_Id);
            setSearchText("");
          }}
          style={{ color: "#E13F57", cursor: "pointer" }}
        >
          {v || "-"}
        </a>
      ),
    },
    { title: "คงเหลือ", dataIndex: "Item_Onhand", width: 90, align: "center", render: (v) => Number(v || 0).toLocaleString() },
    { title: "วันที่ทำรายการล่าสุด", dataIndex: "Last_STC_Date", width: 150, align: "center", render: (v) => formatTH(v) },
    {
      title: "จัดการ",
      key: "action",
      width: 120,
      align: "center",
      render: (_, record) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Button
            type="link"
            size="small"
            icon={<HistoryOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              if (selectedRow?.Item_Id) {
                navigate(`/stock/stock-detail?tab=history&item_id=${selectedRow.Item_Id}&wh_id=${record.WH_Id}`, {
                  state: {
                    from: "/stock/stock-overview",
                    fromLabel: "ดูสต๊อกสินค้า",
                    selectedItemId: selectedRow.Item_Id,
                    selectedWhId: record.WH_Id,
                  },
                });
              }
            }}
            style={{ color: "#E13F57", padding: "0 4px" }}
            title="ประวัติสินค้าเคลื่อนไหว"
          />
        </Space>
      ),
    },
  ];

  const totalOnHand = onhandByWarehouse.reduce((sum, item) => sum + (item.Item_Onhand || 0), 0);

  const columnsAllItems = [
    { title: "#", width: 60, align: "center", render: (_, record) => record?.__globalIndex ?? "-" },
    { title: "หมวดหมู่", dataIndex: "Item_Group", width: 130, render: (v) => groupMap[v] || "-" },
    {
      title: "ชื่อสินค้า", dataIndex: "Item_Name",
      render: (v) => (
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <CopyOutlined
            style={{ color: "#9ca3af", fontSize: 13, flexShrink: 0, cursor: "pointer" }}
            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(v || "").then(() => message.success("คัดลอกแล้ว")); }}
          />
          {v || "-"}
        </span>
      ),
    },
    { title: "หน่วย", dataIndex: "Item_Unit_IN", width: 90, align: "center", render: (v) => v || "-" },
    {
      title: "คงเหลือ",
      dataIndex: "Item_Onhand",
      width: 100,
      align: "center",
      render: (v) => {
        const q = Number(v || 0);
        let color = "success";
        if (q <= 0) color = "error";
        else if (q <= 10) color = "warning";
        return <Tag color={color} style={{ fontSize: 13 }}>{q.toLocaleString()}</Tag>;
      },
    },
    {
      title: "จุดสั่งซื้อ",
      dataIndex: "Item_PoPoint",
      width: 100,
      align: "center",
      render: (v) => {
        const q = Number(v || 0);
        return q > 0 ? q.toLocaleString() : "-";
      },
    },
  ];

  const breadcrumbs = [{ label: "สต๊อกสินค้า", link: "/stock/stock-overview" }, { label: "ดูสต๊อกสินค้า" }];

  return (
    <div className="WarehouseStock">
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="btn btn-toggle-sidebar"
        style={{ position: "fixed", top: "1rem", left: isSidebarOpen ? "190px" : "-15px", zIndex: 1001, background: "transparent", border: "none", color: "#E13F57", fontSize: "20px" }}
      >
        {isSidebarOpen ? "✖" : "☰"}
      </button>
      <div className="wrapper">
        <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
        <div
          className={`main-panel ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
          style={{ height: "100vh", overflow: "hidden" }}
        >
          <Topbar breadcrumbs={breadcrumbs} />
          <div className="container">
            <div className="page-inner" style={{ marginTop: "40px" }}>
              <div className="pt-3 pb-3 pl-2 pr-3" style={{ display: "flex", flexDirection: "column", minHeight: "calc(100vh - 120px)" }}>
                <div className="bg-white rounded-lg shadow-sm px-4 py-4 md:px-6 md:py-5" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
                  <Row gutter={[16, 12]} style={{ flexShrink: 0 }}>
                    <Col xs={24} xl={12} style={{ display: "flex", alignItems: "center" }}>
                      <Title level={3} style={{ marginBottom: 0 }}>สินค้าคงเหลือในคลัง</Title>
                    </Col>
                    <Col xs={24} xl={12} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        style={{ background: "#E13F57", borderColor: "#E13F57" }}
                        onClick={() => setProductModalOpen(true)}
                      >
                        สร้าง รายการสินค้า
                      </Button>
                      <Button
                        type="primary"
                        style={{ background: "#E13F57", borderColor: "#E13F57" }}
                        onClick={() => navigate("/stock/stock-documents")}
                      >
                        เอกสารงานคลัง
                      </Button>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]} style={{ flex: 1, minHeight: 0, marginTop: 16 }}>
                    <Col xs={24} xl={12} style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
                      <div style={{ flexShrink: 0, marginBottom: 12, minHeight: 76 }}>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4, paddingRight: 79 }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, color: "#E13F57", cursor: "pointer", whiteSpace: "nowrap" }}>
                            <input
                              type="checkbox"
                              checked={filterLowStock}
                              onChange={(e) => setFilterLowStock(e.target.checked)}
                              style={{ accentColor: "#E13F57", cursor: "pointer" }}
                            />
                            คงเหลือน้อยกว่าจุดสั่งซื้อ
                          </label>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <span style={{ fontSize: 13, color: "#222", lineHeight: 1 }}>เลือกคลัง</span>
                            <Select
                              placeholder="ทั้งหมด"
                              style={{ width: 180 }}
                              allowClear
                              value={filterWH}
                              onChange={(val) => { setFilterWH(val ?? null); setSelectedRow(null); setOnhandRows([]); }}
                              options={[{ value: null, label: "ทั้งหมด" }, ...whDataList.map((w) => ({ value: w.WH_Id, label: w.WH_Name }))]}
                            />
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <span style={{ fontSize: 13, color: "#222", lineHeight: 1 }}>เลือกหมวดหมู่</span>
                            <Select
                              placeholder="ทั้งหมด"
                              style={{ width: 160 }}
                              allowClear
                              value={filterGroup}
                              onChange={handleGroup}
                              options={groups.map((g) => ({ value: g.Group_Id, label: g.Group_Name }))}
                            />
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 120 }}>
                            <span style={{ fontSize: 13, color: "#222", lineHeight: 1 }}>คำค้นหา</span>
                            <Input
                              prefix={<SearchOutlined />}
                              placeholder="ค้นหาชื่อหรือรหัสสินค้า..."
                              allowClear
                              value={searchText}
                              onChange={(e) => handleSearch(e.target.value)}
                              onPressEnter={handleSearchCommit}
                              onClear={() => { setSearchText(""); setSelectedRow(null); setOnhandRows([]); fetchStock("", filterGroup); }}
                            />
                          </div>
                        </div>
                      </div>
                      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                        <DataTable
                          columns={columnsAllItems}
                          dataSource={filtered}
                          loading={loading}
                          rowKey="Item_Id"
                          defaultPageSize={100}
                          pageSizeOptions={[10, 20, 50, 100]}
                          alwaysScroll
                          initialPage={_s.itemsPage || 1}
                          initialPageSize={_s.itemsPageSize || 100}
                          onPageChange={(page) => saveSession({ itemsPage: page })}
                          onPageSizeChange={(size) => saveSession({ itemsPageSize: size, itemsPage: 1 })}
                          onRow={(record) => ({ onClick: () => SelectedRowData(record) })}
                          rowClassName={(record) =>
                            record.Item_Id === selectedRow?.Item_Id ? "ant-table-row-selected" : ""
                          }
                        />
                      </div>
                    </Col>
                    <Col xs={24} xl={12} style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
                      <div style={{ flexShrink: 0, marginBottom: 12, minHeight: 76, display: "flex", alignItems: "flex-end", paddingBottom: 2 }}>
                        {selectedRow
                          ? <span style={{ fontSize: 15, fontWeight: 500, color: "#111827" }}>• {selectedRow.Item_Name}</span>
                          : <span style={{ fontSize: 14, color: "#9ca3af" }}>เลือกสินค้าจากตารางด้านซ้าย</span>
                        }
                      </div>
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", border: "1px solid #f0f0f0", borderRadius: 8, overflow: "hidden", background: "#fff", minHeight: 0 }}>
                        {selectedRow ? (
                          <>
                            <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                              <Table
                                size="middle"
                                columns={columnsOnHand}
                                dataSource={onhandByWarehouse}
                                rowKey={(r) => `${r?.Item_Id}-${r?.WH_Id}`}
                                pagination={false}
                                scroll={{ y: onhandByWarehouse.length > 8 ? 450 : undefined, x: "max-content" }}
                              />
                            </div>
                            <div className="data-table-footer" style={{ flexShrink: 0, fontSize: 14, minHeight: 54 }}>
                              <span>จำนวน: {onhandByWarehouse.length.toLocaleString()} รายการ</span>
                              <span style={{ fontWeight: 600 }}>รวมคงเหลือ: {totalOnHand.toLocaleString()}</span>
                            </div>
                          </>
                        ) : (
                          <div style={{ flex: 1 }} />
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProductModal
        open={productModalOpen}
        setOpen={setProductModalOpen}
        onClose={() => setProductModalOpen(false)}
        onSubmit={async (payload) => {
          const data = await submitItems(payload);
          if (data?.ok) {
            setProductModalOpen(false);
            await fetchStock();
          }
        }}
        onDataChanged={async () => {
          await fetchStock();
        }}
        mode="create"
        initialData={null}
      />
    </div>
  );
}
